import React, { useCallback, useEffect, useState } from "react";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { money } from "../../utils/format";

type SelectOption = { id: number; label: string };

type CashFlowLine = {
  lineType: string;
  description: string;
  amount: number;
};

type CashFlowReport = {
  competenceYear: number;
  competenceMonth: number;
  bankAccountId: number | null;
  initialBalance: number;
  operating: CashFlowLine[];
  investing: CashFlowLine[];
  financing: CashFlowLine[];
  finalBalance: number;
  netChange: number;
  freeCashFlow: number;
};

const monthLabels = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export const DFCPage: React.FC = () => {
  const { client } = useAuth();
  const [bankAccounts, setBankAccounts] = useState<SelectOption[]>([]);
  const [bankAccountId, setBankAccountId] = useState<number | "">("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState<CashFlowReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client.get("/modules/bank-accounts?take=100&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
      };
      setBankAccounts(
        payload.data.map((item) => ({
          id: item.id,
          label: `${item.bank.name} Ag ${item.agency} - CC ${item.accountNumber}`,
        }))
      );
    });
  }, [client]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ startYear: year, startMonth: month, endYear: year, endMonth: month });
    if (bankAccountId) params.set("bankAccountId", String(bankAccountId));
    const response = await client.get(`/reports/financial/dfc?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao carregar DFC.");
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as { data?: CashFlowReport } | CashFlowReport;
    const report = "data" in payload ? payload.data : payload;
    setData(report ?? null);
    setLoading(false);
  }, [client, bankAccountId, year, month]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const renderLines = (lines: CashFlowLine[] | undefined) =>
    (lines ?? []).map((line, index) => {
      const isBold = line.lineType.includes("TOTAL") || line.lineType.includes("NET");
      const isHeader = line.lineType.includes("TITLE");
      const flowType = isHeader || isBold ? "-" : line.amount < 0 ? "Saida" : "Entrada";
      return (
        <tr key={index} className={isBold ? "fw-bold" : ""}>
          <td className={line.lineType.startsWith("TOTAL") || line.lineType.startsWith("NET") ? "ps-2" : "ps-4"}>
            {line.description}
          </td>
          <td>
            {!isHeader && !isBold && (
              <span className={`badge ${line.amount < 0 ? "text-bg-danger" : "text-bg-success"}`}>
                {flowType}
              </span>
            )}
          </td>
          <td className="text-end">{money(Math.abs(line.amount))}</td>
        </tr>
      );
    });

  return (
    <div className="container py-4">
      <h1 className="mb-3">Fluxo de Caixa (DFC)</h1>

      <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Conta Bancaria</label>
              <select
                className="form-select form-select-sm"
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">Todas as contas</option>
                {bankAccounts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label form-label-sm mb-1">Ano</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label form-label-sm mb-1">Mes</label>
              <select
                className="form-select form-select-sm"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {monthLabels.map((label, index) => (
                  <option key={index + 1} value={index + 1}>{label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={loadReport}>Buscar</button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <PageState loading={loading} error={error}>
        {data && (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #6c757d" }}>
                  <div className="card-body">
                    <div className="text-muted small">Saldo Inicial</div>
                    <div className="fw-bold fs-5 mt-1">{money(data.initialBalance)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="card shadow-sm h-100"
                  style={{ borderLeft: `4px solid ${data.netChange >= 0 ? "#198754" : "#dc3545"}` }}
                >
                  <div className="card-body">
                    <div className="text-muted small">Variacao Liquida</div>
                    <div className={`fw-bold fs-5 mt-1 ${data.netChange >= 0 ? "text-success" : "text-danger"}`}>
                      {money(data.netChange)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="card shadow-sm h-100"
                  style={{ borderLeft: `4px solid ${data.finalBalance >= 0 ? "#0d6efd" : "#dc3545"}` }}
                >
                  <div className="card-body">
                    <div className="text-muted small">Saldo Final</div>
                    <div className="fw-bold fs-5 mt-1">{money(data.finalBalance)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="card shadow-sm h-100"
                  style={{ borderLeft: `4px solid ${data.freeCashFlow >= 0 ? "#20c997" : "#dc3545"}` }}
                >
                  <div className="card-body">
                    <div className="text-muted small">Free Cash Flow</div>
                    <div className={`fw-bold fs-5 mt-1 ${data.freeCashFlow >= 0 ? "text-success" : "text-danger"}`}>
                      {money(data.freeCashFlow)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="mb-3">
                  {monthLabels[(data.competenceMonth || 1) - 1]} / {data.competenceYear}
                </h5>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "60%" }}>Descricao</th>
                        <th>Tipo</th>
                        <th className="text-end">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="table-secondary fw-bold">
                        <td colSpan={3}>ATIVIDADES OPERACIONAIS</td>
                      </tr>
                      {renderLines(data.operating)}
                      <tr className="table-secondary fw-bold">
                        <td colSpan={3}>ATIVIDADES DE INVESTIMENTO</td>
                      </tr>
                      {renderLines(data.investing)}
                      <tr className="table-secondary fw-bold">
                        <td colSpan={3}>ATIVIDADES DE FINANCIAMENTO</td>
                      </tr>
                      {renderLines(data.financing)}
                      <tr className="table-dark fw-bold">
                        <td>SALDO INICIAL</td>
                        <td>-</td>
                        <td className="text-end">{money(data.initialBalance)}</td>
                      </tr>
                      <tr className="table-dark fw-bold">
                        <td>VARIACAO LIQUIDA</td>
                        <td>-</td>
                        <td className="text-end">{money(Math.abs(data.netChange))}</td>
                      </tr>
                      <tr className="table-dark fw-bold">
                        <td>SALDO FINAL</td>
                        <td>-</td>
                        <td className="text-end">{money(data.finalBalance)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </PageState>
    </div>
  );
};
